import {Dropdown, TextInput, DatePickerInput, Toggle} from '@/components/atoms'
import {SectionTitle} from '@/components/molecules'
import {usePromotion} from '@/context/Promotion/PromotionContext'
import {PromotionType, RewardType} from '@/types/promotion'
import {useEffect} from 'react'
import './_promotionsAttributes.scss'

const PromotionsAttributes = () => {
  const {promotionType, promotion, onResultChange, promotionConfig} = usePromotion()

  const frequencyOptions =
    promotionConfig?.resetFrequency?.map(frequency => ({
      displayText: frequency,
      id: frequency.split(' ')[0],
      isBadge: false,
    })) || []

  const behaviorOptions =
    promotionConfig?.behavior?.map(behavior => ({displayText: behavior, id: behavior, isBadge: false})) || []

  // Initialize default values when component mounts
  useEffect(() => {
    const needsDefaults = promotion.resetFrequencyInDays === 0 || !promotion.behavior

    if (!needsDefaults) return

    const updates = {}

    if (promotion.resetFrequencyInDays === 0) {
      updates.resetFrequencyInDays = parseInt(frequencyOptions[0]?.id)
    }

    if (!promotion.behavior) {
      updates.behavior = behaviorOptions[0]?.displayText
    }

    if (Object.keys(updates).length > 0) {
      onResultChange(updates)
    }
  }, [promotion.resetFrequencyInDays, promotion.behavior, onResultChange])

  const handleInputChange = (value, field) => {
    onResultChange({[field]: value})
  }

  const handleDateChange = (formattedDate, field) => {
    onResultChange({[field]: formattedDate})
  }

  const handleDropdownChange = (value, field) => {
    if (field === 'resetFrequencyInDays') {
      // Convert to integer for reset frequency
      onResultChange({[field]: parseInt(value[0].id)})
    } else if (field === 'behavior') {
      // Store the display text for behavior
      onResultChange({[field]: value[0].displayText})
    }
  }

  const renderAdditionalAttributes = () => {
    // here we determine the selected reward type for Buy product or buy multiple and render field depending upon the
    // selected reward type

    const rewardType = promotion.promotionConditions[0]?.rewards?.[0].type

    switch (rewardType) {
      case RewardType.FREE_PRODUCT:
      case RewardType.GIFT_ITEM:
        return (
          <div className="">
            <TextInput
              feedbackType="info"
              inputSize="medium"
              label="Max Free Items Per Order"
              onChange={value => handleInputChange(parseInt(value) || 0, 'maxFreeItemsPerOrder')}
              onUpdate={value => handleInputChange(parseInt(value) || 0, 'maxFreeItemsPerOrder')}
              placeholderText="10"
              value={promotion.maxFreeItemsPerOrder}
            />
          </div>
        )
      case RewardType.POINTS:
        return (
          <div className="font-semibold">
            <TextInput
              feedbackType="info"
              inputSize="medium"
              label="Max Times Per Order"
              onChange={value => handleInputChange(parseInt(value) || 0, 'maxTimesPerOrder')}
              onUpdate={value => handleInputChange(parseInt(value) || 0, 'maxTimesPerOrder')}
              placeholderText="1"
              value={promotion.maxTimesPerOrder}
            />
          </div>
        )
      default:
        return (
          <div className="font-semibold">
            <TextInput
              feedbackType="info"
              inputSize="medium"
              label="Max Allowed Percentage"
              onChange={value => handleInputChange(parseInt(value) || 0, 'maxAllowedPercentage')}
              onUpdate={value => handleInputChange(parseInt(value) || 0, 'maxAllowedPercentage')}
              placeholderText="100"
              value={promotion.maxAllowedPercentage}
            />
          </div>
        )
    }
  }

  // Helper function to get the selected frequency option
  const getSelectedFrequencyOption = () => {
    const option = frequencyOptions.find(opt => opt.id === promotion.resetFrequencyInDays?.toString())
    return option ? [option] : [frequencyOptions[0]]
  }

  // Helper function to get the selected behavior option
  const getSelectedBehaviorOption = () => {
    const option = behaviorOptions.find(opt => opt.displayText === promotion.behavior)
    return option ? [option] : [behaviorOptions[0]]
  }

  return (
    <div className="mt-4 rounded-lg bg-[#1D1D1D] p-4">
      <SectionTitle label={promotionType} />
      <div className="border-t border-t-[#3A3A3A]"></div>

      <SectionTitle label="Availability" />
      <div className="my-6 grid grid-cols-4 gap-6">
        <div className="">
          <DatePickerInput
            value={promotion.activeStartDate || ''}
            updateValue={formattedDate => handleDateChange(formattedDate, 'activeStartDate')}
            uiSchema={{
              placeholder: 'Start Date',
              label: 'Active Start Date*',
              className: '',
              feedbackType: 'info',
              required: true,
              format: 'yyyy-mm-dd',
              dependentDate: promotion.activeEndDate,
              validationRules: [
                {type: 'startDate', msg: "can't be in the past"},
                {type: 'startDate', endDate: promotion.activeEndDate},
              ],
            }}
          />
        </div>
        <div className="">
          <DatePickerInput
            value={promotion.activeEndDate || ''}
            updateValue={formattedDate => handleDateChange(formattedDate, 'activeEndDate')}
            uiSchema={{
              placeholder: 'End Date',
              label: 'Active End Date*',
              className: '',
              feedbackType: 'info',
              required: true,
              format: 'yyyy-mm-dd',
              dependentDate: promotion.activeStartDate,
              validationRules: [{type: 'endDate', startDate: promotion.activeStartDate}],
            }}
          />
        </div>
        <div className="">
          <div className="mt-8">
            <Toggle
              isDark
              uiSchema={{size: 'small', label: 'Active'}}
              value={promotion.active ?? true}
              updateValue={checked => handleInputChange(checked, 'active')}
            />
          </div>
        </div>
        <div></div>
      </div>

      <div className="border-t border-t-[#3A3A3A]"></div>
      <SectionTitle label="Additional Attributes" />
      <div className="my-6 grid grid-cols-4 gap-6">
        {renderAdditionalAttributes()}
        <div className="font-semibold">
          <TextInput
            feedbackType="info"
            inputSize="medium"
            label="Max Allowed Order Per Store"
            onChange={value => handleInputChange(parseInt(value) || 0, 'maxAllowedOrderPerStore')}
            onUpdate={value => handleInputChange(parseInt(value) || 0, 'maxAllowedOrderPerStore')}
            placeholderText="5"
            value={promotion.maxAllowedOrderPerStore}
          />
        </div>
        {promotionType !== PromotionType.WELCOME_DISCOUNT && (
          <>
            <div className="">
              <label className="dropdown-label text-sm font-normal">Reset Frequency</label>
              <Dropdown
                isJsonWrap={false}
                childList={frequencyOptions}
                isDark={true}
                selection="single"
                isClearable={false}
                updateValue={value => handleDropdownChange(value, 'resetFrequencyInDays')}
                className="dropdown"
                size="medium"
                value={getSelectedFrequencyOption()}
                id="resetFrequency"
              />
            </div>
            <div className="">
              <label className="dropdown-label text-sm font-normal">Behavior</label>
              <Dropdown
                isJsonWrap={false}
                childList={behaviorOptions}
                isDark={true}
                selection="single"
                isClearable={false}
                updateValue={value => handleDropdownChange(value, 'behavior')}
                className="dropdown"
                size="medium"
                value={getSelectedBehaviorOption()}
              />
            </div>
          </>
        )}
        <div></div>
      </div>
    </div>
  )
}

export default PromotionsAttributes
